// Usuarios: alta, rol, activo, tarjeta de asesor en la plantilla del PDF y WhatsApp. Solo correos dados de alta entran.
import { useEffect, useState } from 'react';
import { Pencil, UserPlus } from 'lucide-react';
import { COMISION_POR_ROL, COMISION_TOTAL_OPTIMUS, CuerpoUsuario, OPTIMUS, ROLES_ACTIVOS, Rol, VERDE_DE_ENTRADA } from '@cotizador/compartido';
import type { CuerpoUsuario as TipoCuerpo, Usuario } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, CampoSelect, CampoTexto, Cargando, Chip, ErrorEnLinea, Interruptor, Seccion } from '../../componentes/ui.tsx';
import { ETIQUETA_ROL } from '../../logica/etiquetas.ts';

// Verificación adversarial (13-oct): el alta nace EXTERNO (sin semáforo, 5 % de tope, no autoriza); interno o director se
// eligen a propósito.
const VACIO: TipoCuerpo = { correo: '', nombre: '', rol: 'externo', activo: true, asesor_id_plantilla: null, whatsapp: null };
type CuerpoForma = Omit<TipoCuerpo, 'rol'> & { rol: Usuario['rol'] };

function FormaUsuario({ inicial, alGuardar, alCancelar, esYo }: { inicial: Usuario | null; alGuardar: (u: CuerpoForma) => Promise<void>; alCancelar: () => void; esYo: boolean }) {
  const [f, setF] = useState({
    correo: inicial?.correo ?? '',
    nombre: inicial?.nombre ?? '',
    rol: (inicial?.rol ?? VACIO.rol) as Usuario['rol'],
    activo: inicial?.activo ?? true,
    asesor: inicial?.asesor_id_plantilla ?? '',
    whatsapp: inicial?.whatsapp ? inicial.whatsapp.slice(2) : '',
    comision: inicial?.comision_pct === null || inicial?.comision_pct === undefined ? '' : String(inicial.comision_pct),
    optimus: inicial?.optimus === true,
  });
  // ADENDA A0b: los técnicos ya no se dan de alta; solo se enseña "Técnico" si el usuario ya lo era (para desactivarlo).
  const roles = inicial?.rol === 'tecnico' ? [...ROLES_ACTIVOS, 'tecnico' as const] : ROLES_ACTIVOS;
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function guardar() {
    const tel = f.whatsapp.replace(/\D/g, '');
    const comision = f.comision.trim() === '' ? null : Number(f.comision.replace(',', '.'));
    const cuerpo = {
      comision_pct: comision,
      correo: f.correo,
      nombre: f.nombre.trim(),
      rol: f.rol,
      activo: f.activo,
      asesor_id_plantilla: f.asesor.trim() || null,
      whatsapp: tel ? `52${tel.replace(/^52(?=\d{10}$)/, '')}` : null,
      // OPTIMUS (Diego, 12-oct noche): solo vendedores; un director no es vendedor de Optimus
      optimus: f.optimus && f.rol !== 'director',
    };
    const r = (inicial ? CuerpoUsuario.extend({ rol: Rol }) : CuerpoUsuario).safeParse(cuerpo);
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const i of r.error.issues) {
        const k = String(i.path[0]);
        e[k] = k === 'whatsapp' ? '10 dígitos (sin el 52).' : k === 'asesor_id_plantilla' ? 'Solo minúsculas, números y guiones (ej. miguel).' : k === 'comision_pct' ? 'Un porcentaje de 0 a 50, o vacío.' : i.message;
      }
      setErrores(e);
      return;
    }
    setErrores({});
    setGuardando(true);
    setError(null);
    try {
      await alGuardar(r.data);
    } catch (x) {
      setError(x);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="hoja pila">
      <div className="rejilla-2">
        <CampoTexto etiqueta="Nombre" value={f.nombre} maxLength={80} onChange={(e) => setF({ ...f, nombre: e.target.value })} error={errores.nombre} />
        <CampoTexto etiqueta="Correo" type="email" inputMode="email" autoCapitalize="none" value={f.correo} onChange={(e) => setF({ ...f, correo: e.target.value })} error={errores.correo} ayuda="Es con el que entra (le llega el enlace)." />
        <CampoSelect
          etiqueta="Rol"
          value={f.rol}
          disabled={esYo}
          onChange={(e) => setF({ ...f, rol: e.target.value as Usuario['rol'] })}
          opciones={roles.map((r) => ({ valor: r, texto: ETIQUETA_ROL[r] }))}
          ayuda={
            f.rol === 'tecnico'
              ? 'Los técnicos ya no entran al cotizador: el vendedor captura lo que reportan. Cámbialo o desactívalo.'
              : f.rol === 'director'
                ? 'Puede todo y es el único que ve costos, margen y utilidad.'
                : f.rol === 'externo'
                  ? 'Ve la lista de todas; abre, edita y genera PDF solo de las suyas.'
                  : 'Interno: ve y edita todas las cotizaciones (sin costos ni margen).'
          }
        />
        <CampoTexto
          etiqueta="WhatsApp"
          inputMode="tel"
          placeholder="10 dígitos"
          value={f.whatsapp}
          onChange={(e) => setF({ ...f, whatsapp: e.target.value })}
          error={errores.whatsapp}
          ayuda={f.rol === 'director' ? 'A este WhatsApp le llegan los «Pedir OK a Diego» de los vendedores.' : undefined}
        />
        <CampoTexto
          etiqueta="Comisión (%)"
          inputMode="decimal"
          placeholder={f.optimus && f.rol !== 'director' ? `Vacío = ${OPTIMUS.comision_vendedor_por_omision_pct} % (Optimus)` : `Vacío = la de su rol (${COMISION_POR_ROL[f.rol]} %)`}
          value={f.comision}
          onChange={(e) => setF({ ...f, comision: e.target.value })}
          error={errores.comision_pct}
          ayuda={
            f.optimus && f.rol !== 'director'
              ? `La suya; a cada venta se le suman ${OPTIMUS.comision_aliado_pct} % de ${OPTIMUS.aliado_cobra}. Se resta en el margen que ves tú.`
              : `Lo que se le paga. El margen que ves tú resta al menos ${VERDE_DE_ENTRADA.comision_minima_pct} % (la comisión mínima), aunque aquí diga menos. Por rol: externos 5 %, internos 6 %, director 0 %.`
          }
        />
        <CampoTexto
          etiqueta="Tarjeta de asesor en el PDF"
          placeholder="miguel, juan-carlos…"
          value={f.asesor}
          onChange={(e) => setF({ ...f, asesor: e.target.value })}
          error={errores.asesor_id_plantilla}
          ayuda="El id en la plantilla de la familia (foto y WhatsApp). Sin esto, sus PDF fallan."
        />
      </div>
      <Interruptor etiqueta="Activo" detalle={esYo ? 'No te puedes desactivar a ti mismo.' : 'Un usuario inactivo ya no puede entrar.'} marcado={f.activo} disabled={esYo} alCambiar={(v) => setF({ ...f, activo: v })} />
      <Interruptor
        etiqueta="Optimus"
        detalle={
          f.rol === 'director'
            ? 'Un director no puede ser vendedor de Optimus.'
            : `Vendedor del aliado Optimus: su comisión (${OPTIMUS.comision_vendedor_por_omision_pct} % si la dejas vacía) + ${OPTIMUS.comision_aliado_pct} % de ${OPTIMUS.aliado_cobra}. Sus cotizaciones nuevas suben de precio para que Solarix gane lo mismo que con ${OPTIMUS.comision_base_pct} %, y su tarjeta del PDF dice «${OPTIMUS.leyenda}». Su comisión va de ${COMISION_TOTAL_OPTIMUS.min_pct - OPTIMUS.comision_aliado_pct} a ${COMISION_TOTAL_OPTIMUS.max_pct - OPTIMUS.comision_aliado_pct} %. Las que ya tiene no cambian solas, ni si le cambias la comisión: avisan y traen el botón para ponerse al día. Solo tú lo ves.`
        }
        marcado={f.optimus && f.rol !== 'director'}
        disabled={f.rol === 'director'}
        alCambiar={(v) => setF({ ...f, optimus: v })}
      />
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      <div className="fila-botones fila-botones--fin">
        <Boton variante="fantasma" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton variante="principal" cargando={guardando} onClick={() => void guardar()}>
          {inicial ? 'Guardar' : 'Dar de alta'}
        </Boton>
      </div>
    </div>
  );
}

export function Usuarios() {
  const { usuario: yo } = usarUsuario();
  const [lista, setLista] = useState<Usuario[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editando, setEditando] = useState<string | 'nuevo' | null>(null);
  const cargar = () =>
    api
      .usuarios()
      .then((r) => setLista(r.usuarios))
      .catch(setError);
  useEffect(() => {
    void cargar();
  }, []);

  return (
    <Seccion
      titulo="Usuarios"
      descripcion="Solo entran los correos dados de alta. Los externos solo abren lo suyo; costos y margen solo los ves tú."
      acciones={
        editando !== 'nuevo' ? (
          <Boton variante="principal" icono={<UserPlus aria-hidden />} onClick={() => setEditando('nuevo')}>
            Dar de alta
          </Boton>
        ) : undefined
      }
    >
      <div className="pila">
        {editando === 'nuevo' && (
          <FormaUsuario
            inicial={null}
            esYo={false}
            alCancelar={() => setEditando(null)}
            alGuardar={async (u) => {
              // Alta: el esquema ya revisó que el rol no sea "técnico" (CuerpoUsuario); aquí se vuelve a decir en voz alta.
              if (u.rol === 'tecnico') throw new Error('Los técnicos ya no se dan de alta (el vendedor captura lo que reportan).');
              await api.crearUsuario({ ...u, rol: u.rol });
              setEditando(null);
              await cargar();
            }}
          />
        )}
        <ErrorEnLinea error={error} />
        {!lista && !error && <Cargando renglones={4} />}
        {lista && (
          <ul className="usuarios">
            {lista.map((u) =>
              editando === u.id ? (
                <li key={u.id}>
                  <FormaUsuario
                    inicial={u}
                    esYo={u.id === yo.id}
                    alCancelar={() => setEditando(null)}
                    alGuardar={async (c) => {
                      await api.editarUsuario(u.id, c);
                      setEditando(null);
                      await cargar();
                    }}
                  />
                </li>
              ) : (
                <li key={u.id} className={['usuario', u.activo ? '' : 'usuario--inactivo'].join(' ')}>
                  <span className="usuario__avatar" aria-hidden>
                    {u.nombre
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((p) => p[0]?.toUpperCase() ?? '')
                      .join('')}
                  </span>
                  <span className="usuario__datos">
                    <span className="usuario__nombre">
                      {u.nombre}
                      {u.id === yo.id && <span className="nota-suave"> (tú)</span>}
                    </span>
                    <span className="usuario__correo">{u.correo}</span>
                  </span>
                  <span className="usuario__chips">
                    <Chip tono={u.rol === 'director' ? 'noche' : 'neutro'}>{ETIQUETA_ROL[u.rol]}</Chip>
                    {!u.activo && <Chip tono="rojo">Inactivo</Chip>}
                    {u.optimus && <Chip tono="prusia">Optimus</Chip>}
                    {u.rol !== 'tecnico' && (u.asesor_id_plantilla ? <Chip tono="verde">Tarjeta: {u.asesor_id_plantilla}</Chip> : <Chip tono="ambar">Sin tarjeta en el PDF</Chip>)}
                  </span>
                  <Boton chico variante="fantasma" icono={<Pencil aria-hidden />} aria-label={`Editar a ${u.nombre}`} onClick={() => setEditando(u.id)} />
                </li>
              ),
            )}
          </ul>
        )}
      </div>
    </Seccion>
  );
}
